'use strict';

/**
 * Plansheet's standalone Whiteboards library.
 *
 * Whiteboards (the Moodboard entity) attach polymorphically (OwnerType + IDOwner) or stand alone
 * (empty owner). This page lists every whiteboard in the plan sheet as cards -- standalone ones plus
 * those that belong to a Vision or a Work Item -- and opens any of them in a focused editor. "New
 * whiteboard" makes a standalone board. The owner-attached boards are also editable in place on their
 * entity's detail page; this is the cross-cutting view of all of them.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libLabels = require('../libLabels.js');

function _esc(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pChar]));
}

// A short, locale-friendly day label (e.g. "Jul 3, 2026"). Tolerates an ISO string, a Date, or a
// null. Dates arrive as ISO strings over JSON from both the SQLite (dev) and Postgres (prod) stores.
function _shortDate(pDate)
{
	if (!pDate) { return ''; }
	let tmpStamp = Date.parse(pDate);
	if (isNaN(tmpStamp)) { return String(pDate); }
	return new Date(tmpStamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

class PictViewWhiteboards extends libPictView
{
	_api() { return this.pict.providers.MoodboardAPI; }

	// The board currently open in the editor, read by the editor's Media image source to scope its gallery.
	currentBoardId() { return this._openID || null; }

	_canEdit()
	{
		let tmpApp = this.pict.PictApplication;
		return !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
	}

	// Route entry: render the shell then load the list.
	show()
	{
		this._mode = 'list';
		this._openID = 0;
		this._afterShowOnce = null;
		this.render();
		if (typeof document !== 'undefined')
		{
			let tmpTitle = document.getElementById('Whiteboards-PageTitle');
			if (tmpTitle) { tmpTitle.textContent = libLabels.plural(this.pict, 'Whiteboard', null, 'Whiteboards'); }
		}
		this._loadList();
	}

	_loadList()
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		tmpAPI.listAllForCustomer().then((pRows) => { this._boards = Array.isArray(pRows) ? pRows : []; this._renderList(); });
	}

	// A human label for where a board lives.
	_ownerLabel(pRow)
	{
		if (!pRow) { return 'Standalone'; }
		if (pRow.OwnerType === 'WorkItem') { return 'Work Item #' + pRow.IDOwner; }
		if (pRow.OwnerType === 'Vision' || pRow.IDVision) { return 'Vision #' + (pRow.IDOwner || pRow.IDVision); }
		if (pRow.OwnerType === 'Feature') { return 'Feature #' + pRow.IDOwner; }
		if (pRow.OwnerType === 'Module') { return 'Module #' + pRow.IDOwner; }
		if (pRow.OwnerType === 'User' || pRow.IDUser) { return 'Profile'; }
		if (pRow.OwnerType) { return pRow.OwnerType + ' #' + pRow.IDOwner; }
		return 'Standalone';
	}

	// A compact when-label for a card: "Created <date>", plus "Updated <date>" when the board has been
	// touched on a later day than it was made (so a never-edited board reads as just its create date).
	_dateLabel(pRow)
	{
		if (!pRow) { return ''; }
		let tmpCreated = _shortDate(pRow.CreateDate);
		let tmpUpdated = _shortDate(pRow.UpdateDate);
		let tmpParts = [];
		if (tmpCreated) { tmpParts.push('Created ' + tmpCreated); }
		if (tmpUpdated && tmpUpdated !== tmpCreated) { tmpParts.push('Updated ' + tmpUpdated); }
		return tmpParts.join(' · ');
	}

	_renderList()
	{
		if (typeof document === 'undefined') { return; }
		let tmpListWrap = document.getElementById('Whiteboards-ListWrap');
		let tmpEditor = document.getElementById('Whiteboards-Editor');
		if (tmpListWrap) { tmpListWrap.style.display = ''; }
		if (tmpEditor) { tmpEditor.style.display = 'none'; }
		let tmpHost = document.getElementById('Whiteboards-List');
		if (!tmpHost) { return; }
		let tmpSelf = "_Pict.views['Plansheet-Whiteboards']";
		let tmpCanEdit = this._canEdit();
		let tmpCards = (this._boards || []).map((pB) =>
		{
			let tmpDates = this._dateLabel(pB);
			return '<div class="wbl-card" onclick="' + tmpSelf + '.openBoard(' + pB.IDMoodboard + ')">' +
				'<button class="wbl-card-fs" title="View full screen" onclick="event.stopPropagation();_Pict.PictApplication.openBoardFullscreen(' + pB.IDMoodboard + ')">&#10530;</button>' +
				'<div class="wbl-card-body">' +
					'<div class="wbl-card-name">' + _esc(pB.Name || ('Whiteboard #' + pB.IDMoodboard)) + '</div>' +
					'<div class="wbl-card-owner">' + _esc(this._ownerLabel(pB)) + '</div>' +
					(tmpDates ? '<div class="wbl-card-dates">' + _esc(tmpDates) + '</div>' : '') +
				'</div>' +
				(tmpCanEdit ? '<button class="wbl-card-edit" title="Rename" onclick="event.stopPropagation();' + tmpSelf + '.renameBoard(' + pB.IDMoodboard + ')">&#9998;</button>' : '') +
				(tmpCanEdit ? '<button class="wbl-card-del" title="Delete" onclick="event.stopPropagation();' + tmpSelf + '.deleteBoard(' + pB.IDMoodboard + ')">&times;</button>' : '') +
			'</div>';
		}).join('');
		tmpHost.innerHTML = tmpCards || '<div class="wbl-empty">No whiteboards yet. Make one with “New whiteboard”.</div>';
	}

	// Tell the user when an action fails instead of leaving the button looking dead (the create used to
	// have no catch at all, so a refused or errored createForOwner was an unhandled, silent rejection).
	_toast(pMsg, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMsg, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	newBoard()
	{
		if (!this._canEdit()) { return; }
		let tmpAPI = this._api();
		if (!tmpAPI)
		{
			this._toast('Could not add a whiteboard: the whiteboard service is not available. Reload the page and try again.', 'error');
			return;
		}
		tmpAPI.createForOwner('', 0, 'Untitled whiteboard')
			.then((pRow) =>
			{
				if (pRow && pRow.IDMoodboard) { this.openBoard(pRow.IDMoodboard); }
				else { this._toast('Could not add a whiteboard: the server did not return a new record.', 'error'); }
			})
			.catch((pErr) => { this._toast('Could not add a whiteboard: ' + ((pErr && pErr.message) || pErr), 'error'); });
	}

	// Opening a board is a NAVIGATION: set the URL to /whiteboard/:id and let that route render it, so the
	// open board deep-links and survives a reload (it used to open imperatively, leaving the URL on
	// /whiteboards -- a reload dropped you back to the list). A one-shot after-open follow-up (e.g. seeding
	// a photo onto a fresh board) is stashed for showBoard to run once the editor mounts.
	openBoard(pID, fAfter)
	{
		this._afterShowOnce = (typeof fAfter === 'function') ? fAfter : null;
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showWhiteboard === 'function') { tmpApp.showWhiteboard(Number(pID) || 0); }
		else { this.showBoard(pID); } // no app/router (e.g. a bare test harness): open directly
	}

	// Route entry for /whiteboard/:id. Renders the shell (so the editor container exists even on a cold
	// reload / pasted deep-link), switches to the editor, loads the board, and titles the page after its
	// name (so bookmarks + history read as the board). Runs any stashed after-open follow-up once mounted.
	showBoard(pID)
	{
		this._openID = Number(pID) || 0;
		this._mode = 'edit';
		this.render();
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		let tmpListWrap = document.getElementById('Whiteboards-ListWrap');
		let tmpEditor = document.getElementById('Whiteboards-Editor');
		if (tmpListWrap) { tmpListWrap.style.display = 'none'; }
		if (tmpEditor) { tmpEditor.style.display = ''; }
		let tmpSelf = this;
		tmpAPI.read(this._openID).then((pRow) =>
		{
			let tmpBoard = tmpAPI.loadBoard(pRow);
			let tmpName = (pRow && pRow.Name) || 'Whiteboard';
			let tmpTitleEl = document.getElementById('Whiteboards-Editor-Title');
			if (tmpTitleEl) { tmpTitleEl.textContent = tmpName; }
			let tmpApp = tmpSelf.pict.PictApplication;
			if (tmpApp && typeof tmpApp._setPageTitle === 'function') { tmpApp._setPageTitle(tmpName); }
			let tmpView = tmpSelf.pict.views['Whiteboard-Page-Edit'];
			if (tmpView) { tmpView.render(); tmpView.setBoard(tmpBoard); }
			// Let the editor mount, then run any follow-up (e.g. dropping a photo onto a fresh board).
			let tmpAfter = tmpSelf._afterShowOnce;
			tmpSelf._afterShowOnce = null;
			if (typeof tmpAfter === 'function') { setTimeout(() => tmpAfter(tmpView), 200); }
		});
	}

	// Open the hidden file input behind the "From photo" button.
	pickPhoto()
	{
		if (typeof document === 'undefined') { return; }
		let tmpInput = document.getElementById('Whiteboards-PhotoInput');
		if (tmpInput) { tmpInput.value = ''; tmpInput.click(); }
	}

	onPhotoPicked(pInput)
	{
		let tmpFile = pInput && pInput.files && pInput.files[0];
		if (tmpFile) { this.newFromPhoto(tmpFile); }
	}

	// "Just an image": make a standalone whiteboard whose content is a photo (a snap of a real in-person
	// whiteboard, say). Create the board, open it, and drop the image in via the editor's own upload
	// path (stored as Media owned by the board).
	newFromPhoto(pFile)
	{
		if (!this._canEdit() || !pFile) { return; }
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		let tmpName = (pFile.name || 'Photo').replace(/\.[^.]+$/, '').slice(0, 80) || 'Photo';
		tmpAPI.createForOwner('', 0, tmpName).then((pRow) =>
		{
			if (!pRow || !pRow.IDMoodboard) { return; }
			this.openBoard(pRow.IDMoodboard, (pView) =>
			{
				if (!pView) { return; }
				// Present the photo full-bleed: a jumbotron fits the width of a frame seeded from the
				// image, so a single-photo board reads as the photo filling the board when viewed.
				if (typeof pView.setDisplayStyle === 'function') { pView.setDisplayStyle('jumbotron'); }
				if (typeof pView.addImageFiles === 'function') { pView.addImageFiles([pFile]); }
			});
		});
	}

	// Autosave hook wired to Whiteboard-Page-Edit (debounced by the editor itself).
	onBoardSaved(pBoard)
	{
		let tmpAPI = this._api();
		if (tmpAPI && this._openID) { tmpAPI.saveBoard(this._openID, pBoard).catch(() => {}); }
	}

	// Back to the list = navigate to /whiteboards, so the URL leaves the board and a reload lands on the
	// list. The /whiteboards route calls show(), which repaints the list.
	back()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showWhiteboards === 'function') { tmpApp.showWhiteboards(); }
		else { this._mode = 'list'; this._loadList(); }
	}

	fullscreenCurrent()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && this._openID && typeof tmpApp.openBoardFullscreen === 'function') { tmpApp.openBoardFullscreen(this._openID); }
	}

	// Rename a whiteboard from the library card: a small text-input dialog seeded with the current name;
	// Enter or Save commits. Works the same for a photo-seeded board (it is an ordinary standalone board).
	// On save, patch the cached row + repaint (no list re-fetch), mirroring the entity-detail board rename.
	renameBoard(pID)
	{
		if (!this._canEdit() || !pID) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpRow = (this._boards || []).find((pB) => Number(pB.IDMoodboard) === Number(pID));
		let tmpCurrent = (tmpRow && tmpRow.Name) || '';
		if (!tmpModal || typeof tmpModal.show !== 'function')
		{
			this._toast('Could not rename: the dialog service is not available. Reload the page and try again.', 'error');
			return;
		}
		let tmpInputEl = null;
		tmpModal.show(
		{
			title: 'Rename whiteboard',
			width: '420px',
			closeable: true,
			content: '<div style="display:flex;flex-direction:column;gap:6px;">' +
				'<label style="font-size:12px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:var(--theme-color-text-secondary,#5b6376);">Name</label>' +
				'<input type="text" id="wbl-rename-input" maxlength="200" value="' + _esc(tmpCurrent) + '" ' +
				'style="padding:8px 10px;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;font-size:14px;' +
				'background:var(--theme-color-background-panel,#fff);color:var(--theme-color-text-primary,#2c3140);">' +
				'</div>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Save', Style: 'primary' }
			],
			onOpen: (pDialog) =>
			{
				tmpInputEl = pDialog.querySelector('#wbl-rename-input');
				if (!tmpInputEl) { return; }
				tmpInputEl.focus();
				tmpInputEl.select();
				// Enter commits by clicking Save (keeps a single dismiss path).
				tmpInputEl.addEventListener('keydown', (pKeyEvent) =>
				{
					if (pKeyEvent.key === 'Enter')
					{
						pKeyEvent.preventDefault();
						let tmpSaveBtn = pDialog.querySelector('[data-hash="save"]');
						if (tmpSaveBtn) { tmpSaveBtn.click(); }
					}
				});
			}
		}).then((pHash) =>
		{
			if (pHash !== 'save') { return; }
			let tmpName = (tmpInputEl && tmpInputEl.value != null) ? tmpInputEl.value.trim() : '';
			if (!tmpName) { this._toast('A whiteboard needs a name.', 'error'); return; }
			if (tmpName === tmpCurrent) { return; }
			let tmpAPI = this._api();
			if (!tmpAPI) { return; }
			tmpAPI.update(pID, { Name: tmpName })
				.then((pUpdated) =>
				{
					// Patch the cached row (name + fresh UpdateDate) and repaint, no board-blob re-fetch.
					let tmpPatch = (this._boards || []).find((pB) => Number(pB.IDMoodboard) === Number(pID));
					if (tmpPatch)
					{
						tmpPatch.Name = tmpName;
						if (pUpdated && pUpdated.UpdateDate) { tmpPatch.UpdateDate = pUpdated.UpdateDate; }
					}
					this._renderList();
					this._toast('Whiteboard renamed.', 'success');
				})
				.catch((pErr) => this._toast('Rename failed: ' + ((pErr && pErr.message) || pErr), 'error'));
		});
	}

	deleteBoard(pID)
	{
		if (!this._canEdit() || !pID) { return; }
		libFrameworkClient.delJSON(this.pict, '/1.0/Moodboard/' + pID)
			.then(() => this._loadList())
			.catch(() => {});
	}
}

module.exports = PictViewWhiteboards;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Whiteboards',
	DefaultRenderable: 'Plansheet-Whiteboards',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.wbl { display: flex; flex-direction: column; height: 100%; overflow: auto; }
		.wbl-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
		.wbl-head-actions { display: flex; align-items: center; gap: 8px; }
		.wbl-h { margin: 0; font-size: 1.4em; color: var(--theme-color-text-primary, #1d2230); }
		.wbl-btn { padding: 8px 16px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.9em; }
		.wbl-btn:hover { filter: brightness(1.05); }
		.wbl-btn-secondary { background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); border-color: var(--theme-color-border-default, #d8dde6); }
		.wbl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }
		.wbl-card { position: relative; border: 1px solid var(--theme-color-border-default, #e3e6ec); border-radius: 10px; padding: 16px; min-height: 92px; background: var(--theme-color-background-panel, #fff); cursor: pointer; display: flex; flex-direction: column; justify-content: flex-end; }
		.wbl-card:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); box-shadow: 0 2px 10px rgba(20,40,80,0.08); }
		.wbl-card-name { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); font-size: 0.98em; overflow: hidden; text-overflow: ellipsis; }
		.wbl-card-owner { font-size: 0.76em; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); margin-top: 4px; }
		.wbl-card-dates { font-size: 0.72em; color: var(--theme-color-text-secondary, #5b6376); opacity: 0.8; margin-top: 3px; }
		.wbl-card-del { position: absolute; top: 6px; right: 8px; border: none; background: transparent; color: var(--theme-color-text-secondary, #9aa3b2); font-size: 16px; cursor: pointer; line-height: 1; }
		.wbl-card-del:hover { color: var(--theme-color-status-error, #c0353a); }
		.wbl-card-edit { position: absolute; top: 6px; right: 30px; border: none; background: transparent; color: var(--theme-color-text-secondary, #9aa3b2); font-size: 14px; cursor: pointer; line-height: 1; }
		.wbl-card-edit:hover { color: var(--theme-color-brand-primary, #3b6fd4); }
		.wbl-card-fs { position: absolute; top: 6px; left: 8px; border: none; background: transparent; color: var(--theme-color-text-secondary, #9aa3b2); font-size: 14px; cursor: pointer; line-height: 1; }
		.wbl-card-fs:hover { color: var(--theme-color-brand-primary, #3b6fd4); }
		.wbl-empty { padding: 28px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; color: var(--theme-color-text-secondary, #5b6376); }
		.wbl-editor-head { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; }
		.wbl-editor-title { font-size: 1.1em; font-weight: 600; color: var(--theme-color-text-primary, #1d2230); }
		.wbl-canvas { height: 70vh; min-height: 360px; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-secondary, #f4f6f9); }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Whiteboards',
			Template: /*html*/`
<div class="ps-page wbl">
	<div id="Whiteboards-ListWrap">
		<div class="wbl-head">
			<h2 class="wbl-h" id="Whiteboards-PageTitle">Whiteboards</h2>
			<div class="wbl-head-actions">
				<button class="wbl-btn wbl-btn-secondary" onclick="_Pict.views['Plansheet-Whiteboards'].pickPhoto()">From photo</button>
				<button class="wbl-btn" onclick="_Pict.views['Plansheet-Whiteboards'].newBoard()">New whiteboard</button>
				<input type="file" id="Whiteboards-PhotoInput" accept="image/*" style="display:none;" onchange="_Pict.views['Plansheet-Whiteboards'].onPhotoPicked(this)">
			</div>
		</div>
		<div class="wbl-grid" id="Whiteboards-List"></div>
	</div>
	<div id="Whiteboards-Editor" style="display:none;">
		<div class="wbl-editor-head">
			<button class="wbl-btn wbl-btn-secondary" onclick="_Pict.views['Plansheet-Whiteboards'].back()">&larr; All whiteboards</button>
			<span class="wbl-editor-title" id="Whiteboards-Editor-Title"></span>
			<button class="wbl-btn wbl-btn-secondary" onclick="_Pict.views['Plansheet-Whiteboards'].fullscreenCurrent()">Full screen</button>
		</div>
		<div class="wbl-canvas" id="Whiteboard-Page-Canvas"></div>
	</div>
</div>`
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-Whiteboards', TemplateHash: 'Plansheet-Whiteboards', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};
